import { FormSectionWithRows } from '@strata/react-form-section';
import { KpiLarge } from '@strata/react-kpi';
import { MessagingBlock } from '@strata/react-messaging-block';
import PropTypes from 'prop-types';

import { OrtfOutboundGridSection } from './OrtfOutboundGridSection';

const BreakdownTotals = ({ title, totals }) => (
    <FormSectionWithRows
        sectionTitle={title}
        rowOneContent={
            <div className="flex__gap--large" style={{ flexWrap: 'wrap' }}>
                {totals.map(({ label, value }) => (
                    <KpiLarge key={label} label={label} value={value} />
                ))}
            </div>
        }
    />
);

const BreakdownSections = ({
    columns,
    emptyMessage,
    isLoading,
    sectionEmptyMessage,
    sections,
    sortProperties,
}) => {
    if (sections.length === 0) {
        if (isLoading) {
            return (
                <OrtfOutboundGridSection
                    columns={columns}
                    data={[]}
                    isLoading
                    sortProperties={sortProperties}
                />
            );
        }

        return <MessagingBlock size={MessagingBlock.Sizes.MEDIUM} secondaryText={emptyMessage} />;
    }

    return sections.map((section) => (
        <OrtfOutboundGridSection
            key={section.title}
            className="margin__bottom--x-large"
            columns={columns}
            data={section.rows}
            isLoading={isLoading}
            sortProperties={sortProperties}
            title={section.title}
            emptyMessage={section.emptyMessage ?? sectionEmptyMessage}
        />
    ));
};

export const ScriptCountBreakdown = ({
    totalsTitle = 'Totals',
    totals = [],
    columns,
    sortProperties,
    sections = [],
    sectionsEmptyMessage = 'No data available.',
    sectionEmptyMessage = 'No data available.',
    sideSection = undefined,
    sideSectionColumns = undefined,
    sideSectionSortProperties = undefined,
    isLoading = false,
}) => (
    <>
        {totals.length > 0 ? <BreakdownTotals title={totalsTitle} totals={totals} /> : null}
        {sideSection ? (
            <div className="sds_details-page-overview margin__top--large">
                <div className="sds_details-page-overview__container" style={{ flex: '5 0' }}>
                    <BreakdownSections
                        columns={columns}
                        emptyMessage={sectionsEmptyMessage}
                        isLoading={isLoading}
                        sectionEmptyMessage={sectionEmptyMessage}
                        sections={sections}
                        sortProperties={sortProperties}
                    />
                </div>
                <div className="sds_details-page-overview__container" style={{ flex: '4 0' }}>
                    <OrtfOutboundGridSection
                        columns={sideSectionColumns ?? columns}
                        data={sideSection.rows}
                        isLoading={isLoading}
                        sortProperties={sideSectionSortProperties ?? sortProperties}
                        title={sideSection.title}
                        emptyMessage={sideSection.emptyMessage ?? sectionsEmptyMessage}
                    />
                </div>
            </div>
        ) : (
            <div className="margin__top--large">
                <BreakdownSections
                    columns={columns}
                    emptyMessage={sectionsEmptyMessage}
                    isLoading={isLoading}
                    sectionEmptyMessage={sectionEmptyMessage}
                    sections={sections}
                    sortProperties={sortProperties}
                />
            </div>
        )}
    </>
);

const totalMetricPropType = PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
});

const sectionPropType = PropTypes.shape({
    title: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    emptyMessage: PropTypes.string,
});

ScriptCountBreakdown.propTypes = {
    totalsTitle: PropTypes.string,
    totals: PropTypes.arrayOf(totalMetricPropType),
    columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    sortProperties: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            desc: PropTypes.bool,
        }),
    ).isRequired,
    sections: PropTypes.arrayOf(sectionPropType),
    sectionsEmptyMessage: PropTypes.string,
    sectionEmptyMessage: PropTypes.string,
    sideSection: PropTypes.shape({
        title: PropTypes.string.isRequired,
        rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        emptyMessage: PropTypes.string,
    }),
    sideSectionColumns: PropTypes.arrayOf(PropTypes.shape({})),
    sideSectionSortProperties: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            desc: PropTypes.bool,
        }),
    ),
    isLoading: PropTypes.bool,
};

BreakdownTotals.propTypes = {
    title: PropTypes.string.isRequired,
    totals: PropTypes.arrayOf(totalMetricPropType).isRequired,
};

BreakdownSections.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    emptyMessage: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    sectionEmptyMessage: PropTypes.string.isRequired,
    sections: PropTypes.arrayOf(sectionPropType).isRequired,
    sortProperties: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            desc: PropTypes.bool,
        }),
    ).isRequired,
};
